import React from 'react';
import { useHistory } from 'react-router-dom';
import { useDemo } from '../../state/DemoContext';
import StatCard from '../atoms/StatCard';
import Gauge from '../atoms/Gauge';
import './overview.css';

const Overview = () => {
  const { state } = useDemo();
  const history = useHistory();

  const connectedConnectors = state.connectors.filter(c => c.status === 'connected').length;
  const totalAssetValue = state.assets.reduce((sum, asset) => sum + asset.value, 0);
  const availableOffers = state.offers.filter(o => o.status === 'available').length;
  const borrowingBase = state.borrowingBase;
  
  const utilizationRate = borrowingBase ? 
    (borrowingBase.currentBorrowingBase / borrowingBase.maxBorrowingBase) * 100 : 0;

  const handleConnectSystems = () => {
    history.push('/jo-xyz-demo/connect');
  };

  const handleViewAssets = () => {
    history.push('/jo-xyz-demo/assets');
  };

  const handleViewOffers = () => {
    history.push('/jo-xyz-demo/offers');
  };

  const recentActivity = [
    {
      id: 1,
      type: 'system',
      message: 'Salesforce connection established',
      timestamp: '2 minutes ago',
      status: 'success'
    },
    {
      id: 2,
      type: 'agent',
      message: 'Valuation Agent completed analysis',
      timestamp: '15 minutes ago',
      status: 'success'
    },
    {
      id: 3,
      type: 'offer',
      message: 'New offer received from First National Bank',
      timestamp: '1 hour ago',
      status: 'info'
    }
  ];

  return (
    <div className="jo-xyz-demo-overview-page">
      <div className="jo-xyz-demo-overview-page__header">
        <h1>Asset-Backed Lending Dashboard</h1>
        <p className="jo-xyz-demo-overview-page__subtitle">
          Monitor your borrowing capacity, manage assets, and track lending opportunities
        </p>
      </div>

      {/* Key Metrics */}
      <div className="jo-xyz-demo-overview-page__metrics">
        <div className="jo-xyz-demo-grid jo-xyz-demo-grid--4">
          <StatCard
            title="Connected Systems"
            value={`${connectedConnectors}/${state.connectors.length}`}
            subtitle="Required: 2 of 2"
            icon="🔗"
            color={connectedConnectors >= 2 ? 'success' : 'warning'}
          />
          <StatCard
            title="Total Asset Value"
            value={totalAssetValue}
            subtitle={`${state.assets.length} assets registered`}
            icon="📦"
            color="primary"
          />
          <StatCard
            title="Available Offers"
            value={availableOffers}
            subtitle="From multiple lenders"
            icon="📋"
            color="accent"
          />
          <StatCard
            title="Borrowing Base"
            value={borrowingBase?.maxBorrowingBase || 0}
            subtitle="Maximum capacity"
            icon="💰"
            color="success"
          />
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="jo-xyz-demo-overview-page__content">
        <div className="jo-xyz-demo-overview-page__left">
          {/* Capacity Gauge */}
          <div className="jo-xyz-demo-overview-page__section">
            <div className="jo-xyz-demo-card">
              <div className="jo-xyz-demo-card__header">
                <h3 className="jo-xyz-demo-card__title">Borrowing Capacity</h3>
                <p className="jo-xyz-demo-card__subtitle">Current utilization vs. maximum capacity</p>
              </div>
              <div className="jo-xyz-demo-capacity-gauge">
                <Gauge
                  value={borrowingBase?.currentBorrowingBase || 0}
                  max={borrowingBase?.maxBorrowingBase || 1}
                  size={200}
                  strokeWidth={12}
                  label="Utilization"
                  subtitle={`$${((borrowingBase?.currentBorrowingBase || 0) / 1000000).toFixed(1)}M of $${((borrowingBase?.maxBorrowingBase || 0) / 1000000).toFixed(1)}M`}
                  color={utilizationRate > 80 ? 'danger' : utilizationRate > 60 ? 'warning' : 'success'}
                />
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="jo-xyz-demo-overview-page__section">
            <div className="jo-xyz-demo-card">
              <div className="jo-xyz-demo-card__header">
                <h3 className="jo-xyz-demo-card__title">Quick Actions</h3>
                <p className="jo-xyz-demo-card__subtitle">Common tasks and next steps</p>
              </div>
              <div className="jo-xyz-demo-quick-actions">
                <button 
                  className="jo-xyz-demo-btn jo-xyz-demo-btn--primary jo-xyz-demo-btn--lg"
                  onClick={handleConnectSystems}
                  disabled={connectedConnectors >= 2}
                >
                  {connectedConnectors >= 2 ? '✓ Systems Connected' : 'Connect Systems'}
                </button>
                <button 
                  className="jo-xyz-demo-btn jo-xyz-demo-btn--secondary jo-xyz-demo-btn--lg"
                  onClick={handleViewAssets}
                >
                  View Asset Registry
                </button>
                <button 
                  className="jo-xyz-demo-btn jo-xyz-demo-btn--accent jo-xyz-demo-btn--lg"
                  onClick={handleViewOffers}
                  disabled={availableOffers === 0}
                >
                  {availableOffers > 0 ? `View ${availableOffers} Offers` : 'No Offers Available'}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="jo-xyz-demo-overview-page__right">
          {/* Recent Activity */}
          <div className="jo-xyz-demo-overview-page__section">
            <div className="jo-xyz-demo-card">
              <div className="jo-xyz-demo-card__header">
                <h3 className="jo-xyz-demo-card__title">Recent Activity</h3>
                <p className="jo-xyz-demo-card__subtitle">Latest system updates and notifications</p>
              </div>
              <div className="jo-xyz-demo-activity-feed">
                {recentActivity.map((activity) => (
                  <div key={activity.id} className="jo-xyz-demo-activity-item">
                    <div className={`jo-xyz-demo-activity-item__icon jo-xyz-demo-activity-item__icon--${activity.status}`}>
                      {activity.type === 'system' && '🔗'}
                      {activity.type === 'agent' && '🤖'}
                      {activity.type === 'offer' && '📋'}
                    </div>
                    <div className="jo-xyz-demo-activity-item__content">
                      <div className="jo-xyz-demo-activity-item__message">
                        {activity.message}
                      </div>
                      <div className="jo-xyz-demo-activity-item__timestamp">
                        {activity.timestamp}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* System Status */}
          <div className="jo-xyz-demo-overview-page__section">
            <div className="jo-xyz-demo-card">
              <div className="jo-xyz-demo-card__header">
                <h3 className="jo-xyz-demo-card__title">System Status</h3>
                <p className="jo-xyz-demo-card__subtitle">Integration and service health</p>
              </div>
              <div className="jo-xyz-demo-system-status">
                {state.connectors.map((connector) => (
                  <div key={connector.id} className="jo-xyz-demo-system-item">
                    <div className="jo-xyz-demo-system-item__icon">
                      {connector.icon}
                    </div>
                    <div className="jo-xyz-demo-system-item__content">
                      <div className="jo-xyz-demo-system-item__name">
                        {connector.name}
                      </div>
                      <div className={`jo-xyz-demo-system-item__status jo-xyz-demo-system-item__status--${connector.status}`}>
                        {connector.status === 'connected' ? 'Connected' : 'Disconnected'}
                      </div>
                    </div>
                    <div className="jo-xyz-demo-system-item__records">
                      {connector.records > 0 && (
                        <span className="jo-xyz-demo-system-item__count">
                          {connector.records.toLocaleString()} records
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Overview;
